import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Container, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { getAcademyBaseUrl, loadAcademyManifest } from './academyContent';

const AcademyHomePage = () => {
  const location = useLocation();
  const academyBaseUrl = useMemo(() => getAcademyBaseUrl(), [location.search]);
  const [manifest, setManifest] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setStatus('loading');
    setError(null);

    loadAcademyManifest(academyBaseUrl)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setManifest(data);
        setStatus('loaded');
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err.message || 'Unable to load Academy content.');
        setStatus('error');
      });

    return () => {
      isMounted = false;
    };
  }, [academyBaseUrl]);

  const tracks = manifest?.tracks || [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.75rem', md: '2rem' },
            fontWeight: 600,
            letterSpacing: '-0.03em',
            color: 'var(--dwlf-text)',
            mb: 1,
          }}
        >
          Academy
        </Typography>
        <Typography 
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.9rem',
            color: 'var(--dwlf-ink-50)',
          }}
        >
          Learn the DWLF system step by step with interactive lessons
        </Typography>
      </Box>

      {status === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {status === 'loading' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1">Loading Academy tracks...</Typography>
        </Paper>
      )}

      {status === 'loaded' && tracks.length === 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1">Tracks will appear here once content is published.</Typography>
        </Paper>
      )}

      {tracks.map((track) => (
        <Paper key={track.id} id={track.id} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {track.title}
          </Typography>
          {track.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {track.description}
            </Typography>
          )}
          <List disablePadding>
            {track.lessons?.map((lesson) => (
              <ListItem
                key={lesson.slug}
                button
                component={Link}
                to={`/learn/${lesson.slug}`}
                sx={{
                  borderRadius: '12px',
                  mb: 1,
                  backgroundColor: 'background.default',
                  '&:hover': {
                    backgroundColor: 'background.surface',
                  },
                }}
              >
                <ListItemText
                  primary={lesson.title}
                  secondary={lesson.estimatedMinutes ? `${lesson.estimatedMinutes} min` : null}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      ))}
    </Container>
  );
};

export default AcademyHomePage;
